type SavedColumn = { id: string; visible: boolean; order: number };

/**
 * Merges a saved view's columns into the grid's current ones, tolerating schema
 * drift: columns the schema no longer has are dropped, saved order/visibility
 * are restored, and every column the view did NOT know about (added in a later
 * release) is spliced in after its nearest already-placed predecessor in
 * `current` — its natural slot, not the far right. Such a column takes its
 * visibility from `defaults`, not from `current`: the view decides the grid, so
 * an unsaved toggle of the new column must not survive into it.
 */
export function mergeSavedColumns<C extends { id: string; visible: boolean }>(
  current: C[],
  saved: SavedColumn[],
  defaults: { id: string; visible: boolean }[],
): C[] {
  const savedById = new Map(saved.map((c) => [c.id, c]));
  const defaultById = new Map(defaults.map((c) => [c.id, c]));
  const surviving: C[] = current.map((c) => {
    const visible = savedById.get(c.id)?.visible ?? defaultById.get(c.id)?.visible ?? c.visible;
    return { ...c, visible };
  });
  const merged: C[] = surviving
    .filter((c) => savedById.has(c.id))
    .sort((a, b) => savedById.get(a.id)!.order - savedById.get(b.id)!.order);
  for (let i = 0; i < surviving.length; i++) {
    const c = surviving[i];
    if (savedById.has(c.id)) continue;
    let insertIdx = 0;
    for (let j = i - 1; j >= 0; j--) {
      const predIdx = merged.findIndex((m) => m.id === surviving[j].id);
      if (predIdx !== -1) {
        insertIdx = predIdx + 1;
        break;
      }
    }
    merged.splice(insertIdx, 0, c);
  }
  return merged;
}
